import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { finalize, Observable, tap } from 'rxjs';
import { LanguageStateModel } from '../../models/language-state.model';
import { Language } from '../../models/language.interface';
import { LanguagesService } from '../../services/languages.service';
import { LoadLanguages, SetCurrentLanguage, SetPreviouslySelectedLanguage } from './language.actions';

@State<LanguageStateModel>({
    name: 'language',
    defaults: {
        currentLanguage: { abbreviation: 'en', label: 'English' },
        languages: [],
        isLoadingLanguages: false
    }
})
@Injectable()
export class LanguageState {
    private readonly languageService = inject(LanguagesService);
    private readonly translate = inject(TranslateService);

    @Selector()
    static currentLanguage(state: LanguageStateModel): Language {
        return state.currentLanguage;
    }

    @Selector()
    static languages(state: LanguageStateModel): Language[] {
        return state.languages || [];
    }

    @Selector()
    static isLoadingLanguages(state: LanguageStateModel): boolean {
        return state.isLoadingLanguages;
    }

    @Action(LoadLanguages)
    loadLanguages(ctx: StateContext<LanguageStateModel>): Observable<{ languages: Language[] }> {
        ctx.patchState({ isLoadingLanguages: true });
        return this.languageService.getLanguages().pipe(
            tap(languages => {
                ctx.patchState({ languages: languages.languages || [] });
            }),
            finalize(() => {
                ctx.patchState({ isLoadingLanguages: false });
            })
        );
    }

    @Action(SetCurrentLanguage)
    setCurrentLanguage(ctx: StateContext<LanguageStateModel>, action: SetCurrentLanguage): void {
        const { languages } = ctx.getState();
        const language = languages.find(lang => lang.abbreviation === action.languageCode);
        if (language) {
            this.translate.use(language.abbreviation);
            ctx.patchState({ currentLanguage: language });
            localStorage.setItem('language', JSON.stringify(language.abbreviation));
        }
    }

    @Action(SetPreviouslySelectedLanguage)
    setPreviouslySelectedLanguage(ctx: StateContext<LanguageStateModel>): void {
        const previouslySelectedLanguage = localStorage.getItem('language');
        if (previouslySelectedLanguage) {
            ctx.dispatch(new SetCurrentLanguage(JSON.parse(previouslySelectedLanguage)));
        }
    }
}
